import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRightLeftIcon,
  DatabaseIcon,
  ExternalLinkIcon,
  PauseIcon,
  PlayIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  bridge,
  task as taskApi,
  track,
  type TaskDetail,
  type TaskItem,
  type TaskStatus,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { PageShell } from '@/components/PageShell';
import { TaskProgressCard, taskStatusLabel } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RowList } from '@/components/RowList';
import { SectionHeader } from '@/components/SectionHeader';
import { CARD, HINT, MUTED, NUM } from '@/components/ui';

/**
 * 任务详情。
 *
 * 搬小书没有 Web 详情页（它的「详情」是跳对应控制台页），这里是按同样风格
 * 组合出来的：状态/进度卡 + 汇总 + 逐条明细（迁移任务）+ 行内操作。
 */

function statusTone(status: TaskStatus): string {
  if (status === 'running') return 'text-foreground font-medium';
  if (status === 'failed') return 'text-destructive';
  return 'text-muted-foreground';
}

function formatTime(ms?: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 各类任务的名称与对应控制台页（相对路由前缀）。 */
const TYPE_META: Record<string, { label: string; console: string }> = {
  migration: { label: '账号迁移', console: 'migration' },
  backup: { label: '备份', console: 'backup' },
  visibility: { label: '批量设置可见性', console: 'notes' },
  post_action: { label: '笔记操作', console: 'notes' },
  comment_clean: { label: '清空评论', console: 'notes' },
};

/** 任务详情。搬小音与薯薯共用，`app` 决定路由前缀。 */
export function TaskDetailPage({ app = 'bxy' }: { app?: 'bxy' | 'shushu' }) {
  const base = `/${app}`;
  const { taskId = '' } = useParams();
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [items, setItems] = useState<TaskItem[]>([]);
  const [failedOnly, setFailedOnly] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 埋点：页面访问
  useEffect(() => {
    track.pageOpen('task_detail');
  }, []);

  const refresh = useCallback(async () => {
    if (!taskId) return;
    try {
      const next = await taskApi.get(taskId);
      setDetail(next);
      if (next.hasItems) {
        const listed = await taskApi.items(taskId, { failedOnly, limit: 200 });
        setItems(listed.items);
      } else {
        setItems([]);
      }
      setError(null);
    } catch (e) {
      setError(describeError(e));
    }
  }, [failedOnly, taskId]);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoaded(true);
    })();
  }, [refresh]);

  // 任务状态变化由原生推送，不轮询
  useEffect(() => {
    const off = bridge.on('task.changed', () => {
      void refresh();
    });
    return off;
  }, [refresh]);

  const run = useCallback(
    async (action: 'pause' | 'resume' | 'delete') => {
      if (!detail || busy) return;
      setBusy(true);
      setError(null);
      try {
        if (action === 'pause') {
          await taskApi.pause(detail.task.taskId);
        } else if (action === 'resume') {
          await taskApi.resume(detail.task.taskId);
        } else {
          await taskApi.delete(detail.task.taskId);
        }
        await refresh();
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, detail, refresh],
  );

  const taskItem = detail?.task;
  const summary = detail?.summary;
  const total = summary?.total ?? 0;
  const done = (summary?.migrated ?? 0) + (summary?.failed ?? 0);
  const running = taskItem?.status === 'running';
  const paused = taskItem?.status === 'paused';
  const finished = taskItem ? !running && !paused && taskItem.status !== 'idle' : false;
  const consolePath = `${base}/${TYPE_META[taskItem?.type ?? '']?.console ?? 'backup'}`;

  return (
    <PageShell title="任务详情" description={taskItem?.name ?? taskId} backTo={`${base}/tasks`}>
      {!loaded ? <PageSkeleton kind="taskDetail" /> : null}

      {taskItem ? (
        <>
          <TaskProgressCard
            title={taskStatusLabel(taskItem.status, { idle: '排队中' }) ?? taskItem.status}
            right={total > 0 ? `${done} / ${total}` : undefined}
            ratio={total > 0 ? done / total : undefined}
            paused={paused}
            finished={finished && taskItem.status === 'completed'}
            bar={total > 0 && !finished}
            icon={
              taskItem.status === 'failed' ? (
                <span className="text-destructive flex size-5 items-center justify-center">
                  <DatabaseIcon className="size-5" />
                </span>
              ) : undefined
            }
            detail={
              <span className={`${statusTone(taskItem.status)}`}>
                {taskItem.accountNickname ?? taskItem.accountId ?? ''}
                {taskItem.failedCount ? ` · 失败 ${taskItem.failedCount}` : ''}
              </span>
            }
            current={taskItem.error ? describeError(taskItem.error) : null}
            action={
              <div className="flex flex-wrap gap-2 ios:flex-col ios:*:w-full">
                {running ? (
                  <Button variant="secondary" loading={busy} onClick={() => void run('pause')}>
                    <PauseIcon data-icon="inline-start" />
                    暂停
                  </Button>
                ) : null}
                {paused || taskItem.status === 'failed' ? (
                  <Button loading={busy} onClick={() => void run('resume')}>
                    <PlayIcon data-icon="inline-start" />
                    继续
                  </Button>
                ) : null}
                {taskItem.type === 'migration' && !running ? (
                  <Button
                    variant="ghost"
                    loading={busy}
                    className="text-destructive hover:bg-destructive/10 ios:order-last"
                    onClick={() => void run('delete')}
                  >
                    <Trash2Icon data-icon="inline-start" />
                    删除
                  </Button>
                ) : null}
                <Button asChild variant="outline">
                  <Link to={consolePath}>
                    <ExternalLinkIcon data-icon="inline-start" />
                    打开控制台
                  </Link>
                </Button>
              </div>
            }
          />

          <Card className={CARD}>
            <SectionHeader title="概览" />
            <div className="grid grid-cols-2 gap-3">
              {[
                ['类型', TYPE_META[taskItem.type]?.label ?? taskItem.type],
                ['状态', taskStatusLabel(taskItem.status, { idle: '排队中' }) ?? taskItem.status],
                ['成功', `${summary?.migrated ?? 0}`],
                ['失败', `${summary?.failed ?? 0}`],
              ].map(([label, value]) => (
                <div key={label} className="bg-muted/60 rounded-icon px-3 py-2.5">
                  <p className="text-caption1 text-muted-foreground">{label}</p>
                  <p className={`text-subheadline mt-0.5 font-semibold ${NUM}`}>{value}</p>
                </div>
              ))}
            </div>
            <p className={`${MUTED} mt-2`}>
              开始 {formatTime(taskItem.startedAt ?? taskItem.createdAt)} · 结束{' '}
              {formatTime(taskItem.completedAt)}
            </p>
          </Card>

          {detail?.hasItems ? (
            <Card className={CARD}>
              <SectionHeader
                title={`明细${items.length > 0 ? `（${items.length}）` : ''}`}
                trailing={
                  <Button
                    size="sm"
                    variant={failedOnly ? 'default' : 'outline'}
                    onClick={() => setFailedOnly((v) => !v)}
                  >
                    {failedOnly ? '只看失败' : '全部'}
                  </Button>
                }
              />
              {items.length === 0 ? (
                <p className={MUTED}>
                  {failedOnly ? '没有失败项' : '暂无明细'}
                </p>
              ) : (
                <RowList>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-muted/60 flex items-start gap-3 rounded-control px-3.5 py-3 ios:rounded-none ios:bg-transparent ios:px-0"
                    >
                      <span className="bg-background flex size-9 shrink-0 items-center justify-center rounded-full">
                        {taskItem.type === 'migration' ? (
                          <ArrowRightLeftIcon className="size-4" />
                        ) : (
                          <DatabaseIcon className="size-4" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <strong className="text-subheadline truncate font-medium ios:text-body">
                            {item.title || item.id}
                          </strong>
                          <span className="text-caption1 text-muted-foreground shrink-0">
                            {item.type}
                          </span>
                        </div>
                        <p className={`${HINT} mt-0.5`}>
                          {item.status === 'failed'
                            ? item.error
                              ? describeError(item.error)
                              : '失败'
                            : item.status === 'migrated'
                              ? taskItem.type === 'migration' ? '已迁移' : '已完成'
                              : '待处理'}
                        </p>
                      </div>
                    </div>
                  ))}
                </RowList>
              )}
            </Card>
          ) : (
            <Card className={CARD}>
              <SectionHeader title="明细" />
              <p className={MUTED}>
                备份任务不逐条落库（只保留计数与断点游标），所以没有逐条明细可看。
              </p>
            </Card>
          )}
        </>
      ) : null}

      {error ? <p className="text-destructive px-1 text-sm">{error}</p> : null}
    </PageShell>
  );
}
